import AppointmentComposeAdapter from 'owa-addins-apis/lib/adapters/AppointmentComposeAdapter';
import AppointmentReadAdapter from 'owa-addins-apis/lib/adapters/AppointmentReadAdapter';
import AttachmentStatusEnum from 'owa-addins-apis/lib/apis/attachments/AttachmentStatusEnum';
import MessageComposeAdapter from 'owa-addins-apis/lib/adapters/MessageComposeAdapter';
import MessageReadAdapter from 'owa-addins-apis/lib/adapters/MessageReadAdapter';
import ApiError from 'owa-addins-apis/lib/apis/ApiError';
import ApiErrorCode from 'owa-addins-apis/lib/apis/ApiErrorCode';
import AppendOnSend from 'owa-addins-apis/lib/apis/body/AppendOnSend';
import InternetHeaders from 'owa-addins-apis/lib/apis/internetHeaders/InternetHeaders';
import { ExtensibilityNotification } from 'owa-addins-apis/lib/apis/notification/ExtensibilityNotification';
import { getExtensibilityNotification } from 'owa-addins-apis/lib/apis/notification/ExtensibilityNotificationManager';
import RecipientFieldEnum from 'owa-addins-apis/lib/apis/recipients/RecipientFieldEnum';
import { CoercionType } from 'owa-addins-apis/lib/apis/setData/CoercionType';
import { AdapterSharedProperties } from 'owa-addins-apis/lib/apis/sharedProperties/SharedProperties';
import OnHostItemChangedStatus from 'owa-addins-boot/lib/schema/OnHostItemChangedStatus';
import isTranslatorEnabled from 'owa-addins-outlook-translate/lib/isTranslatorEnabled';
import PersistedAddinCommand from 'owa-addins-persistent/lib/schema/PersistedAddinCommand';
import { isAnyUilessAddinRunning } from 'owa-addins-store/lib/utils/uilessUtils';
import { AddinBarViewProps } from 'owa-addins-view/lib/components/AddinBarView';
import { TaskPaneCollectionProps } from 'owa-addins-view/lib/components/TaskPaneCollection';
import convertToIContextualMenuItem from 'owa-addins-view/lib/utils/convertToIContextualMenuItem';
import { SelectionType } from 'owa-addins-view/lib/utils/navigations/SelectionType';
import SendFeedbackContainer from 'owa-addins-view/lib/utils/SendFeedbackContainer';
import { createLazyComponent, LazyImport, LazyModule, LazyAction } from 'owa-bundling';
import * as React from 'react';
import {
    getComposeHostItemIndex,
    getReadHostItemIndex,
} from 'owa-addins-store/lib/utils/hostItemIndexUtils';
import {
    LocationIdentifier,
    LocationType,
} from 'owa-addins-apis/lib/apis/location/LocationDetails';
import {
    AttachmentDetails,
    TypeOfAttachment,
} from 'owa-addins-apis/lib/apis/attachments/AttachmentDetails';

const lazyModule = new LazyModule(() => import(/* webpackChunkName: "Addins" */ './lazyIndex'));

// apis
export let lazyRemoveExtensibilityNotification = new LazyImport(
    lazyModule,
    m => m.removeExtensibilityNotification
);
export let lazyUpdatePersistentNotifications = new LazyImport(
    lazyModule,
    m => m.updatePersistentNotifications
);
export let lazyCloseTaskPaneAddinCommand = new LazyImport(
    lazyModule,
    m => m.closeTaskPaneAddinCommand
);

// events
export let lazyTriggerAppointmentTimeChangedEvent = new LazyImport(
    lazyModule,
    m => m.triggerAppointmentTimeChangedEvent
);
export let lazyTriggerRecipientsChangedEvent = new LazyImport(
    lazyModule,
    m => m.triggerRecipientsChangedEvent
);
export let lazyTriggerRecurrenceChangedEvent = new LazyImport(
    lazyModule,
    m => m.triggerRecurrenceChangedEvent
);
export let lazyTriggerLocationsChangedEvent = new LazyImport(
    lazyModule,
    m => m.triggerLocationsChangedEvent
);
export let lazyTriggerAttachmentsChangedEvent = new LazyImport(
    lazyModule,
    m => m.triggerAttachmentsChangedEvent
);

// boot
export let lazyDeinitializeAddinsForItem = new LazyImport(
    lazyModule,
    m => m.deinitializeAddinsForItem
);
export let lazyInitializeAddinsForItem = new LazyImport(lazyModule, m => m.initializeAddinsForItem);
export let lazyInitializeExtensibilityContext = new LazyImport(
    lazyModule,
    m => m.initializeExtensibilityContext
);
export let lazyOnHostItemChanged = new LazyImport(lazyModule, m => m.onHostItemChanged);
export let lazyWhenItemHasContextualAddinKeywords = new LazyImport(
    lazyModule,
    m => m.whenItemHasContextualAddinKeywords
);

// outlook-translate
export let lazyLaunchTranslateCommand = new LazyImport(lazyModule, m => m.launchTranslateCommand);

// persistent
export let lazySetPersistedAddin = new LazyImport(lazyModule, m => m.setPersistedAddin);

// store
export let lazyShouldAddinsForceFullCompose = new LazyImport(
    lazyModule,
    m => m.shouldForceFullCompose
);

// view
export let AddinBarView = createLazyComponent(lazyModule, m => m.AddinBarView);
export let TaskPaneCollection = createLazyComponent(lazyModule, m => m.TaskPaneCollection);

export let lazyOnComposeClose = new LazyImport(lazyModule, m => m.onComposeClose);
export let lazyOpenContextualCallout = new LazyImport(lazyModule, m => m.openContextualCallout);
export let lazyLaunchAddinCommand = new LazyImport(lazyModule, m => m.launchAddinCommand);
export let lazyAddinCommandSurfaceItemsObserver = new LazyImport(
    lazyModule,
    m => m.observeAddinCommandSurfaceItems
);
export let lazyOnItemNavigation = new LazyImport(lazyModule, m => m.onItemNavigation);
export let lazyAllowItemSendEvent = new LazyImport(lazyModule, m => m.allowItemSendEvent);

//Autorun actions
export let lazyOnCompose = new LazyAction(lazyModule, m => m.onCompose);

// store
export { default as shouldAddinsActivate } from 'owa-addins-store/lib/utils/shouldAddinsActivate';

export {
    AdapterSharedProperties,
    AddinBarViewProps,
    AppointmentComposeAdapter,
    AppointmentReadAdapter,
    AttachmentStatusEnum,
    CoercionType,
    convertToIContextualMenuItem,
    getExtensibilityNotification,
    ApiErrorCode,
    ApiError,
    OnHostItemChangedStatus,
    MessageComposeAdapter,
    MessageReadAdapter,
    React,
    TaskPaneCollectionProps,
    getComposeHostItemIndex,
    getReadHostItemIndex,
    isAnyUilessAddinRunning,
    RecipientFieldEnum,
    AttachmentDetails,
    TypeOfAttachment,
    ExtensibilityNotification,
    isTranslatorEnabled,
    SendFeedbackContainer,
    SelectionType,
    PersistedAddinCommand,
    InternetHeaders,
    AppendOnSend,
    LocationIdentifier,
    LocationType,
};
